/**
 * Phone Gantt source — same Nanode missions the Schedule list already shows.
 * Layout math stays in dispatch-core `buildGanttBoard` (CEL#### / F=ferry).
 */
import {
  buildGanttBoard,
  readLiveFleet,
  deskBehaviorOf,
  dutyViewsFor,
  hydrateDutyRoster,
  hydrateGanttLaneMode,
  hydrateGanttTimeDisplay,
  hydrateGanttTimeZoom,
  resolveDeskLocalClock,
  deskLocalScheduleHint,
  type DeskLocalClock,
  type AircraftSpec,
  type CompanyProfile,
  type DutyRoster,
  type DutyView,
  type GanttBoard,
  type GanttLane,
  type GanttLaneMode,
  type GanttRange,
  type GanttTimeDisplay,
  type Mission,
  type TripAssignment,
} from "@northstar/dispatch-core";

export type CompanionGanttCrew = {
  id: string;
  name: string;
};

export type CompanionGanttSource = {
  /** Same ids as the schedule trip list. Not a second board. */
  missionIds: string[];
  missions: Mission[];
  fleet: AircraftSpec[];
  assignments: TripAssignment[];
  crew: CompanionGanttCrew[];
  dutyViews: DutyView[];
  timeDisplay: GanttTimeDisplay;
  laneMode: GanttLaneMode;
  showLegend: boolean;
  showDutyBands: boolean;
  /** Desk Settings time scale. 1× is the phone’s fit width; higher stretches the swipe axis. */
  timeZoom: number;
  enforceDutyClock: boolean;
  /** IANA zone for Local, or UTC when the driving airport has no timezone. */
  timeZone: string;
  /** Caption for the Local tab. Names the field, or the fail-closed Zulu label. */
  localHint: string;
  localIcao: string | null;
};

export const EMPTY_GANTT_SOURCE: CompanionGanttSource = {
  missionIds: [],
  missions: [],
  fleet: [],
  assignments: [],
  crew: [],
  dutyViews: [],
  timeDisplay: "local",
  laneMode: "aircraft",
  showLegend: true,
  showDutyBands: true,
  timeZoom: 1,
  enforceDutyClock: true,
  ...localFields(resolveDeskLocalClock({ homeIcao: "CYPD" })),
};

export function companionGanttSource(input: {
  missions: Mission[];
  fleet?: AircraftSpec[] | null;
  assignments?: TripAssignment[] | null;
  crew?: CompanionGanttCrew[] | null;
  duty?: DutyRoster | null;
  company?: Partial<Pick<CompanyProfile, "ganttTimeDisplay" | "deskBehavior" | "enforceDutyClock" | "homeIcao">> | null;
  homeIcao?: string | null;
  /** Origin of the trip the phone is following. No live surface sample on the phone. */
  ticketOriginIcao?: string | null;
  parkedIcao?: string | null;
  now?: Date;
}): CompanionGanttSource {
  const missions = input.missions ?? [];
  const ids = new Set(missions.map((mission) => mission.id));
  const prefs = deskBehaviorOf(input.company);
  const homeIcao = input.homeIcao?.trim() || input.company?.homeIcao?.trim() || "";
  const crew = (input.crew ?? []).filter((row) => row.id.trim());
  const duty = input.duty ? hydrateDutyRoster(input.duty) : null;
  return {
    missionIds: missions.map((mission) => mission.id),
    missions,
    fleet: readLiveFleet(input.fleet),
    assignments: (input.assignments ?? []).filter((row) => (row.missionId ? ids.has(row.missionId) : false)),
    crew,
    dutyViews: duty ? dutyViewsFor(duty, crew.map((row) => ({ id: row.id, name: row.name }))) : [],
    timeDisplay: hydrateGanttTimeDisplay(input.company?.ganttTimeDisplay),
    laneMode: hydrateGanttLaneMode(prefs.ganttLaneMode),
    showLegend: prefs.ganttShowLegend,
    showDutyBands: prefs.ganttShowDutyBands,
    timeZoom: hydrateGanttTimeZoom(prefs.ganttTimeZoom),
    enforceDutyClock: input.company?.enforceDutyClock !== false,
    ...localFields(
      resolveDeskLocalClock({
        now: input.now,
        ticketOriginIcao: input.ticketOriginIcao,
        parkedIcao: input.parkedIcao || parkedField(input.fleet),
        homeIcao,
      }),
    ),
  };
}

function parkedField(fleet?: AircraftSpec[] | null): string | null {
  const tails = fleet ?? [];
  const available = tails.find((tail) => tail.status === "available" && tail.lastAirportIcao?.trim());
  const any = available ?? tails.find((tail) => tail.lastAirportIcao?.trim());
  return any?.lastAirportIcao?.trim().toUpperCase() || null;
}

function localFields(clock: DeskLocalClock): Pick<CompanionGanttSource, "timeZone" | "localHint" | "localIcao"> {
  return {
    timeZone: clock.timeZone,
    localHint: deskLocalScheduleHint(clock),
    localIcao: clock.icao,
  };
}

export function phoneGanttBoard(
  source: CompanionGanttSource,
  input: { now?: Date; range?: GanttRange; offset?: number; timeDisplay?: GanttTimeDisplay; laneMode?: GanttLaneMode } = {},
): GanttBoard {
  const laneMode = input.laneMode ?? source.laneMode;
  return buildGanttBoard({
    missions: source.missions,
    fleet: source.fleet,
    assignments: source.assignments,
    crew: laneMode === "crew" ? source.crew : [],
    dutyViews: laneMode === "crew" && source.showDutyBands ? source.dutyViews : [],
    enforceDutyClock: source.enforceDutyClock,
    now: input.now,
    range: input.range ?? "day",
    offset: input.offset ?? 0,
    timeDisplay: input.timeDisplay ?? source.timeDisplay,
    timeZone: source.timeZone,
  });
}

/** Drop empty tails/crew so a phone is not a wall of blank rows. Grounded tails stay. */
export function visiblePhoneLanes(board: GanttBoard, mode: GanttLaneMode): GanttLane[] {
  const lanes = mode === "crew" ? board.crewLanes : board.tailLanes;
  return lanes.filter((lane) => lane.bars.length > 0 || lane.grounded === true);
}

function windowHasTicket(source: CompanionGanttSource, input: {
  now: Date;
  range: GanttRange;
  offset: number;
  timeDisplay: GanttTimeDisplay;
  laneMode: GanttLaneMode;
}): boolean {
  const board = phoneGanttBoard(source, input);
  return visiblePhoneLanes(board, input.laneMode).some((lane) => lane.bars.length > 0);
}

/**
 * Day the desk shows a manual ticket can be the next Zulu day (or the previous
 * local day). If "today" on this phone's clock has no bars, open the nearest
 * window that does. Today wins when it already has a ticket.
 */
export function scheduleWindowOffset(
  source: CompanionGanttSource,
  input: { now: Date; range?: GanttRange; timeDisplay?: GanttTimeDisplay; laneMode?: GanttLaneMode },
): number {
  const range = input.range ?? "day";
  const timeDisplay = input.timeDisplay ?? source.timeDisplay;
  const laneMode = input.laneMode ?? source.laneMode;
  const args = { now: input.now, range, timeDisplay, laneMode };
  if (!source.missions.length || windowHasTicket(source, { ...args, offset: 0 })) return 0;
  const limit = range === "week" ? 8 : 21;
  let best: number | null = null;
  for (let offset = -limit; offset <= limit; offset += 1) {
    if (offset === 0 || !windowHasTicket(source, { ...args, offset })) continue;
    if (best == null || Math.abs(offset) < Math.abs(best) || (Math.abs(offset) === Math.abs(best) && offset > best)) {
      best = offset;
    }
  }
  return best ?? 0;
}
